import { useEffect, useRef } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import {
  knowledgeApi, AcceptEdits, AutopilotStart, BulkJob, ConceptListParams, CuratePayload, PolicyAction, ProposalAction, RelationType,
  ReviewKind,
} from '../api/knowledgeApi'

// The graph changes once a night (the sync after Picasso's build) or when a curator edits a
// concept, so reads are cached generously and mutations invalidate explicitly.
const STALE = 5 * 60 * 1000

const KEYS = {
  stats: ['kg-stats'],
  concepts: (p: ConceptListParams) => ['kg-concepts', p],
  concept: (ucid: string) => ['kg-concept', ucid],
  artworks: (ucid: string, offset: number) => ['kg-concept-artworks', ucid, offset],
  graph: (ucid: string, concepts: number, artworks: number) => ['kg-graph', ucid, concepts, artworks],
  artwork: (artworkId: number) => ['kg-artwork', artworkId],
}

export function useKnowledgeStats() {
  return useQuery({ queryKey: KEYS.stats, queryFn: knowledgeApi.getStats, staleTime: STALE })
}

export function useConcepts(params: ConceptListParams) {
  return useQuery({
    queryKey: KEYS.concepts(params),
    queryFn: () => knowledgeApi.getConcepts(params),
    placeholderData: keepPreviousData,
    staleTime: STALE,
  })
}

export function useConcept(ucid: string | undefined) {
  return useQuery({
    queryKey: KEYS.concept(ucid ?? ''),
    queryFn: () => knowledgeApi.getConcept(ucid!),
    enabled: !!ucid,
    staleTime: STALE,
  })
}

export function useConceptArtworks(ucid: string, offset: number, limit = 60) {
  return useQuery({
    queryKey: KEYS.artworks(ucid, offset),
    queryFn: () => knowledgeApi.getConceptArtworks(ucid, { offset, limit }),
    placeholderData: keepPreviousData,
    staleTime: STALE,
  })
}

export function useConceptGraph(ucid: string | undefined, concepts: number, artworks: number) {
  return useQuery({
    queryKey: KEYS.graph(ucid ?? '', concepts, artworks),
    queryFn: () => knowledgeApi.getGraph(ucid!, { concepts, artworks }),
    enabled: !!ucid,
    staleTime: STALE,
  })
}

export function useArtworkConcepts(artworkId: number | undefined) {
  return useQuery({
    queryKey: KEYS.artwork(artworkId ?? 0),
    queryFn: () => knowledgeApi.getArtworkConcepts(artworkId!),
    enabled: !!artworkId,
    staleTime: STALE,
  })
}

/** Everything a curation can change: the concept, lists, the review queue, the rules. */
function invalidateCuration(qc: ReturnType<typeof useQueryClient>) {
  for (const key of ['kg-concept', 'kg-concepts', 'kg-stats', 'kg-review', 'kg-review-items', 'kg-rules', 'kg-preview',
    'kg-proposals', 'kg-proposal-items']) {
    qc.invalidateQueries({ queryKey: [key] })
  }
}

const errorMessage = (e: unknown) => {
  const err = e as { response?: { data?: { message?: string } }; message?: string }
  return err.response?.data?.message || err.message || 'Something went wrong'
}

export function useCurateConcept(ucid: string) {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: (payload: CuratePayload) => knowledgeApi.curate(ucid, payload),
    onSuccess: () => {
      invalidateCuration(qc)
      toast({ title: 'Concept updated' })
    },
    onError: (e: Error) => toast({ title: 'Could not update concept', description: errorMessage(e), variant: 'destructive' }),
  })
}

export function useCurateMany() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: knowledgeApi.curateMany,
    onSuccess: (r) => {
      invalidateCuration(qc)
      toast({ title: `Updated ${r.data.length} concept${r.data.length === 1 ? '' : 's'}` })
    },
    onError: (e: Error) => toast({ title: 'Could not update concepts', description: errorMessage(e), variant: 'destructive' }),
  })
}

type RuleInput =
  | { action: 'merge'; ucid: string; ucids: string[] }
  | { action: 'separate'; ucid: string; names: string[] }
  | { action: 'reject'; ucid: string; reason?: string }

const RULE_TOAST: Record<RuleInput['action'], string> = {
  merge: 'Merge recorded — the next nightly build applies it',
  separate: 'Split recorded — the next nightly build applies it',
  reject: 'Removal recorded — the next nightly build applies it',
}

export function useRecordRule() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: (r: RuleInput) =>
      r.action === 'merge'
        ? knowledgeApi.merge(r.ucid, r.ucids)
        : r.action === 'separate'
          ? knowledgeApi.separate(r.ucid, r.names)
          : knowledgeApi.reject(r.ucid, r.reason),
    onSuccess: (d, r) => {
      invalidateCuration(qc)
      const revoked = (d.payload.revoked as number[] | undefined)?.length
      toast({
        title: RULE_TOAST[r.action],
        description: revoked ? `It replaces ${revoked} earlier rule${revoked === 1 ? '' : 's'} it contradicted.` : undefined,
      })
    },
    onError: (e: Error) => toast({ title: 'Could not record that', description: errorMessage(e), variant: 'destructive' }),
  })
}

export function useRules(includeRevoked: boolean) {
  return useQuery({ queryKey: ['kg-rules', includeRevoked], queryFn: () => knowledgeApi.getRules(includeRevoked), staleTime: 30_000 })
}

export function useRevokeRule() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: knowledgeApi.revokeRule,
    onSuccess: () => {
      invalidateCuration(qc)
      toast({ title: 'Rule revoked', description: 'The next export stops sending it to the build.' })
    },
    onError: (e: Error) => toast({ title: 'Could not revoke', description: errorMessage(e), variant: 'destructive' }),
  })
}

export function useExportRules() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: knowledgeApi.exportRules,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['kg-rules'] })
      toast({ title: `Exported ${r.rules} rules to the build`, description: `${r.structural} structural, ${r.settings} concept settings` })
    },
    onError: (e: Error) => toast({ title: 'Export failed', description: errorMessage(e), variant: 'destructive' }),
  })
}

// ─── Model proposals ───────────────────────────────────

export function useProposalSummary() {
  return useQuery({ queryKey: ['kg-proposals'], queryFn: knowledgeApi.getProposalSummary, staleTime: 60_000 })
}

export function useProposals(action: ProposalAction | undefined, status: string, offset: number, limit = 30, domainId?: string) {
  return useQuery({
    queryKey: ['kg-proposal-items', action, status, offset, limit, domainId],
    queryFn: () => knowledgeApi.getProposals({ action, status, offset, limit, domainId }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  })
}

const PROPOSAL_TOAST: Record<ProposalAction, string> = {
  merge: 'Merge accepted — the next nightly build applies it',
  split: 'Split accepted — the next nightly build applies it',
  reject: 'Removal accepted — the next nightly build applies it',
  link: 'Relationship decision accepted',
  category: 'Category set',
  generic: 'Weight set',
}

export function useAcceptProposal() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: (a: { id: string; action: ProposalAction; edits?: AcceptEdits }) => knowledgeApi.acceptProposal(a.id, a.edits),
    onSuccess: (_p, a) => {
      invalidateCuration(qc)
      toast({ title: PROPOSAL_TOAST[a.action] })
    },
    onError: (e: Error) => toast({ title: 'Could not accept', description: errorMessage(e), variant: 'destructive' }),
  })
}

export function useRejectProposal() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: (r: { id: string; action: ProposalAction; note?: string }) => knowledgeApi.rejectProposal(r.id, r.note),
    onSuccess: (_p, r) => {
      invalidateCuration(qc)
      toast({
        title: 'Proposal rejected',
        description: r.action === 'merge' ? 'Remembered as “different” — the model will not propose it again.' : undefined,
      })
    },
    onError: (e: Error) => toast({ title: 'Could not reject', description: errorMessage(e), variant: 'destructive' }),
  })
}

export function useAcceptProposals() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: knowledgeApi.acceptProposals,
    onSuccess: (r) => {
      invalidateCuration(qc)
      toast({
        title: `Accepted ${r.accepted}`,
        description: r.failed.length ? `${r.failed.length} could not be applied: ${r.failed[0].message}` : undefined,
        variant: r.failed.length ? 'destructive' : undefined,
      })
    },
    onError: (e: Error) => toast({ title: 'Could not accept', description: errorMessage(e), variant: 'destructive' }),
  })
}

export function useProposalThreshold(action: ProposalAction, minConfidence: number) {
  return useQuery({
    queryKey: ['kg-proposal-threshold', action, minConfidence],
    queryFn: () => knowledgeApi.getProposalThreshold(action, minConfidence),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })
}

const OPEN_JOB = (j: BulkJob) => j.status === 'queued' || j.status === 'running'

/**
 * Background accepts (Cloud Tasks jobs). Polls every 3 s while one is open; when one finishes,
 * the proposal lists, counts and Progress refresh.
 */
export function useBulkJobs() {
  const qc = useQueryClient()
  const open = useRef<Set<number>>(new Set())
  const query = useQuery({
    queryKey: ['kg-bulk-jobs'],
    queryFn: knowledgeApi.getBulkJobs,
    refetchInterval: (q) => ((q.state.data?.data ?? []).some(OPEN_JOB) ? 3000 : false),
  })
  const jobs = query.data?.data
  useEffect(() => {
    if (!jobs) return
    const now = new Set(jobs.filter(OPEN_JOB).map((j) => j.id))
    const finished = [...open.current].some((id) => !now.has(id))
    if (now.size || finished) {
      // Counts move while a job runs; everything else once it finishes.
      qc.invalidateQueries({ queryKey: ['kg-proposals'] })
      if (finished) {
        invalidateCuration(qc)
        qc.invalidateQueries({ queryKey: ['kg-proposal-threshold'] })
        qc.invalidateQueries({ queryKey: ['kg-health'] })
      }
    }
    open.current = now
  }, [jobs, qc])
  return query
}

export function useStartBulkJob() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: knowledgeApi.startBulkJob,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kg-bulk-jobs'] }),
    onError: (e: Error) => toast({ title: 'Could not start', description: errorMessage(e), variant: 'destructive' }),
  })
}

export function useCancelBulkJob() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: knowledgeApi.cancelBulkJob,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kg-bulk-jobs'] }),
    onError: (e: Error) => toast({ title: 'Could not cancel', description: errorMessage(e), variant: 'destructive' }),
  })
}

export function useSyncProposals() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: knowledgeApi.syncProposals,
    onSuccess: (r) => {
      invalidateCuration(qc)
      toast({ title: `${r.inserted} new proposal${r.inserted === 1 ? '' : 's'}`, description: r.stale ? `${r.stale} overtaken by the build` : undefined })
    },
    onError: (e: Error) => toast({ title: 'Could not pull proposals', description: errorMessage(e), variant: 'destructive' }),
  })
}

/** Progress: refreshes every minute while a resolution run is in progress. */
export function useKnowledgeHealth(days = 90) {
  return useQuery({
    queryKey: ['kg-health', days],
    queryFn: () => knowledgeApi.getHealth(days),
    staleTime: 30_000,
    refetchInterval: (q) => (q.state.data?.runs.some((r) => r.status === 'running') ? 60_000 : false),
  })
}

export function useSnapshotHealth() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: knowledgeApi.snapshotHealth,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kg-health'] }),
  })
}

export function useSerRuns() {
  return useQuery({ queryKey: ['kg-ser-runs'], queryFn: knowledgeApi.getSerRuns, staleTime: STALE })
}

export function useRelationEdit() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: (r: { op: 'add' | 'remove'; source: string; target: string; type: RelationType }) =>
      r.op === 'add'
        ? knowledgeApi.addRelation(r.source, r.target, r.type).then(() => undefined)
        : knowledgeApi.removeRelation(r.source, r.target, r.type),
    onSuccess: (_v, r) => {
      qc.invalidateQueries({ queryKey: ['kg-concept'] })
      toast({ title: r.op === 'add' ? 'Relation added' : 'Relation removed' })
    },
    onError: (e: Error) => toast({ title: 'Could not change the relation', description: errorMessage(e), variant: 'destructive' }),
  })
}

// ─── Review queue ──────────────────────────────────────

export function useReviewSummary() {
  return useQuery({ queryKey: ['kg-review'], queryFn: knowledgeApi.getReviewSummary, staleTime: 60_000 })
}

export function useReviewItems(kind: ReviewKind, offset: number, limit = 50) {
  return useQuery({
    queryKey: ['kg-review-items', kind, offset, limit],
    queryFn: () => knowledgeApi.getReviewItems({ kind, offset, limit }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  })
}

export function useDismiss() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: knowledgeApi.dismiss,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['kg-review'] })
      qc.invalidateQueries({ queryKey: ['kg-review-items'] })
      toast({ title: `Dismissed ${r.dismissed}` })
    },
    onError: (e: Error) => toast({ title: 'Could not dismiss', description: errorMessage(e), variant: 'destructive' }),
  })
}

export function useSuggestCategories() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: (limit: number) => knowledgeApi.suggestCategories(limit),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['kg-review-items'] })
      toast({
        title: `Suggested categories for ${r.suggested} concepts`,
        description: `${r.remaining.toLocaleString()} still without a suggestion${r.errors.length ? ` · ${r.errors.length} batch errors` : ''}`,
      })
    },
    onError: (e: Error) => toast({ title: 'Suggestions failed', description: errorMessage(e), variant: 'destructive' }),
  })
}

export function useRefreshReview() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: knowledgeApi.refreshReview,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kg-review'] })
      qc.invalidateQueries({ queryKey: ['kg-review-items'] })
    },
  })
}

// ─── Search preview ────────────────────────────────────

export function useSearchPreview(params: { q: string; mode: 'curated' | 'raw'; publishedOnly: boolean; queryId?: number }) {
  return useQuery({
    queryKey: ['kg-preview', params],
    queryFn: () => knowledgeApi.preview(params),
    enabled: params.q.trim().length > 0,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  })
}

export function useSavedQueries() {
  return useQuery({ queryKey: ['kg-queries'], queryFn: knowledgeApi.getQueries, staleTime: 30_000 })
}

export function useSaveQuery() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: (q: { query: string; notes?: string }) => knowledgeApi.saveQuery(q.query, q.notes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kg-queries'] })
      toast({ title: 'Query saved', description: 'Mark results as right or wrong to build the test set.' })
    },
    onError: (e: Error) => toast({ title: 'Could not save', description: errorMessage(e), variant: 'destructive' }),
  })
}

export function useDeleteQuery() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: knowledgeApi.deleteQuery,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kg-queries'] }),
  })
}

export function useJudge() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (j: { queryId: number; artworkId: number; verdict: 1 | -1 | null }) =>
      knowledgeApi.judge(j.queryId, j.artworkId, j.verdict),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kg-preview'] })
      qc.invalidateQueries({ queryKey: ['kg-queries'] })
    },
  })
}

export function useEvaluate() {
  return useMutation({ mutationFn: knowledgeApi.evaluate })
}

// ─── Explore ───────────────────────────────────────────

export function useArtistSearch(q: string) {
  return useQuery({
    queryKey: ['kg-artists', q],
    queryFn: () => knowledgeApi.searchArtists(q),
    enabled: q.trim().length > 1,
    staleTime: STALE,
  })
}

export function useArtistFingerprint(artistId: number | undefined) {
  return useQuery({
    queryKey: ['kg-artist', artistId],
    queryFn: () => knowledgeApi.getArtistFingerprint(artistId!),
    enabled: !!artistId,
    staleTime: STALE,
  })
}

export function useConceptPath(from: string | undefined, to: string | undefined) {
  return useQuery({
    queryKey: ['kg-path', from, to],
    queryFn: () => knowledgeApi.getPath(from!, to!),
    enabled: !!from && !!to,
    staleTime: STALE,
  })
}

export function useArtworkComparison(a: number | undefined, b: number | undefined) {
  return useQuery({
    queryKey: ['kg-compare', a, b],
    queryFn: () => knowledgeApi.compareArtworks(a!, b!),
    enabled: !!a && !!b,
    staleTime: STALE,
    retry: false,
  })
}

export function useRuns() {
  return useQuery({ queryKey: ['kg-runs'], queryFn: knowledgeApi.getRuns, staleTime: STALE })
}

export function useRunChanges(runId: string | undefined) {
  return useQuery({
    queryKey: ['kg-run', runId],
    queryFn: () => knowledgeApi.getRunChanges(runId!),
    enabled: !!runId,
    staleTime: STALE,
  })
}

// ─── Resolution agent & autopilot ──────────────────────

export function useAgentStats(days = 30) {
  return useQuery({
    queryKey: ['kg-agent', days],
    queryFn: () => knowledgeApi.getAgentStats(days),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    // Live while the model is running or anything is being accepted.
    refetchInterval: (q) =>
      q.state.data && (q.state.data.runs.some((r) => r.status === 'running') || q.state.data.jobs.some(OPEN_JOB)) ? 15_000 : false,
  })
}

const startedText = (started: AutopilotStart[]) => {
  const jobs = started.filter((s) => s.jobId && !s.skipped)
  return jobs.length ? `Accepting ${jobs.map((j) => `${j.total?.toLocaleString()} ${j.action}`).join(', ')} in the background` : 'Nothing new to accept'
}

function invalidateAgent(qc: ReturnType<typeof useQueryClient>) {
  for (const key of ['kg-agent', 'kg-bulk-jobs', 'kg-proposals', 'kg-proposal-threshold']) qc.invalidateQueries({ queryKey: [key] })
}

export function useSetAutopilotPolicy() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ action, change }: { action: PolicyAction; change: { enabled?: boolean; minConfidence?: number } }) =>
      knowledgeApi.setAutopilotPolicy(action, change),
    onSuccess: (r) => {
      invalidateAgent(qc)
      toast({
        title: `Autopilot ${r.policy.enabled ? 'on' : 'off'} for ${r.policy.action}`,
        description: r.policy.enabled ? startedText(r.started) : undefined,
      })
    },
    onError: (e: Error) => toast({ title: 'Could not change autopilot', description: errorMessage(e), variant: 'destructive' }),
  })
}

export function useRunAutopilot() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: (all: boolean) => knowledgeApi.runAutopilot(all),
    onSuccess: (r) => {
      invalidateAgent(qc)
      toast({ title: startedText(r.started) })
    },
    onError: (e: Error) => toast({ title: 'Could not start', description: errorMessage(e), variant: 'destructive' }),
  })
}

export function useGraphOverview() {
  return useQuery({ queryKey: ['kg-overview'], queryFn: knowledgeApi.getOverview, staleTime: STALE })
}

/** Read-only operational status, fresh enough for the daily garden dashboard. */
export function useGardenOperations(days = 7) {
  return useQuery({
    queryKey: ['kg-operations', days],
    queryFn: () => knowledgeApi.getOperations(days),
    staleTime: 15_000,
    refetchInterval: 30_000,
  })
}

export function useDomainAgenda(search = '', offset = 0) {
  return useQuery({
    queryKey: ['kg-domains', search, offset],
    queryFn: () => knowledgeApi.getDomainAgenda(search, offset),
    staleTime: 60_000,
    refetchInterval: 60_000,
  })
}
